import { hooks } from '@wove/react';
import { forwardRef, ReactNode, useImperativeHandle, useRef, useState } from 'react';
import { useEffectCustom } from '../hooks/use-effect-custom';
import { useMutationEffect } from '../hooks/use-mutation-effect';
import { NativeScroll } from '../native-scroll';
import { NativeProps, withNativeProps } from '../utils/native-props';
import { px2DPIpx } from '../utils/pxrem';
import { StickyTabsContext, StickyTabsContextChangeOption } from './context';
import './style.less';

export type StickyTabsProps = {
  onChange: (activeKey?: string) => void;
  offsetHeight?: number;
  // height中number 值为视觉稿高度
  height?: string | number;
  defaultActiveKey?: string;
  children: ReactNode | ReactNode[];
  onScroll?: (event) => void;
} & NativeProps;

export type StickyTabsRefApi = {
  scrollToByKey: (activeKey: string, behavior?: ScrollBehavior) => void;
  updateSticky: () => void;
  scrollTo: (top: number, behavior?: ScrollBehavior) => void;
};

export const StickyTabs = forwardRef<StickyTabsRefApi, StickyTabsProps>((props, ref) => {
  const scollRef = useRef<HTMLDivElement>(null);
  const behaviorRef = useRef<string>('');
  const activeKeyRef = useRef<string>();
  const stickytabsStickyHeight = useRef<number>(0);
  const offsetHeight = parseFloat(px2DPIpx(props.offsetHeight || 0));
  const [stickyItemRefresh, setStickyItemRefresh] = useState(0);

  const stickyTabsItems = useRef<StickyTabsContextChangeOption[]>([]);

  const scrollTo = hooks.useCallbackRef((key: string, behavior: ScrollBehavior) => {
    if (!key) return;
    const target = stickyTabsItems.current.find((item) => item.stickyKey === key);
    if (!target) return;
    behaviorRef.current = 'click';
    scollRef.current?.scrollTo({
      top: target.minOffsetTop - offsetHeight - stickytabsStickyHeight.current,
      behavior,
    });
  });

  useEffectCustom(() => {
    scrollTo(props.defaultActiveKey!, 'auto');
  }, []);

  useImperativeHandle(ref, () => {
    return {
      scrollToByKey: (activeKey, behavior?: ScrollBehavior) => {
        scrollTo(activeKey, behavior || 'smooth');
      },
      updateSticky: () => {
        setStickyItemRefresh(Date.now());
      },
      scrollTo: (top: number, behavior?: ScrollBehavior) => {
        scollRef.current?.scrollTo({
          top: top,
          behavior,
        });
      },
    };
  });

  const onChangeStickyItem = hooks.useCallbackRef((data: StickyTabsContextChangeOption) => {
    const targetIndex = stickyTabsItems.current.findIndex((item) => item.stickyKey === data.stickyKey);
    if (targetIndex >= 0) {
      stickyTabsItems.current[targetIndex] = data;
    } else {
      stickyTabsItems.current.push(data);
    }
  });

  const onScroll = hooks.useThrottleCallback((event) => {
    props?.onScroll?.(event);
    if (behaviorRef.current === 'click') return;
    const scrollTop = (event.target.scrollTop as number) || 0;
    if (scrollTop + stickytabsStickyHeight.current + offsetHeight < stickyTabsItems.current[0].minOffsetTop) {
      if (activeKeyRef.current) {
        activeKeyRef.current = undefined;
        props.onChange(undefined);
        return;
      }
    }
    const target = stickyTabsItems.current.find((item) => {
      const diffHeight = stickytabsStickyHeight.current + scrollTop + offsetHeight;
      return diffHeight >= item.minOffsetTop && diffHeight < item.maxOffsetTop;
    });

    if (target && activeKeyRef.current !== target.stickyKey) {
      activeKeyRef.current = target.stickyKey;
      props.onChange(target.stickyKey);
    }
  });

  const onChangeTabsSticky = hooks.useCallbackRef((data) => {
    stickytabsStickyHeight.current = data.height;
  });

  const onDestroyStickyItem = hooks.useCallbackRef((data) => {
    const targetIndex = stickyTabsItems.current.findIndex((item) => item.stickyKey === data.stickyKey);
    if (targetIndex && targetIndex >= 0) {
      stickyTabsItems.current.splice(targetIndex, 1);
    }
  });
  const onTouchMove = hooks.useCallbackRef(() => {
    behaviorRef.current = 'scroll';
  });

  /**
   * 监听 DOM树变化，重新计算
   */
  useMutationEffect(
    () => {
      setStickyItemRefresh(Date.now());
    },
    scollRef,
    {
      subtree: true,
      childList: true,
      characterData: true,
    },
  );

  return (
    <StickyTabsContext.Provider
      value={{ onChangeStickyItem, onChangeTabsSticky, onDestroyStickyItem, stickyItemRefresh }}
    >
      {withNativeProps(
        props,
        <NativeScroll
          className="v-sticky-tabs"
          onScroll={onScroll}
          onTouchMove={onTouchMove}
          ref={scollRef}
          height={props.height}
        >
          {props.children}
        </NativeScroll>,
      )}
    </StickyTabsContext.Provider>
  );
});
